import React, { useEffect, useRef, useState, useCallback } from "react";
import { api } from "../lib/api";
import { Loader2, Lock, CheckCircle2, AlertTriangle, VideoOff } from "lucide-react";

const HEARTBEAT_MS = 5000;
// Salto oltre il quale si interviene (s). La riproduzione normale avanza a piccoli
// passi — 'timeupdate' scatta ~4 volte al secondo — quindi un delta superiore a
// questa soglia può arrivare solo da un trascinamento sulla timeline.
// Il server tollera 15s: restando sotto, non ci si contraddice a vicenda.
const SEEK_JUMP_THRESHOLD = 2;
// Il video va guardato per intero. Il server applica una piccola tolleranza sugli
// ultimi istanti, che spesso non sono raggiungibili con il tag <video>.
const COMPLETION_THRESHOLD = 100;
// L'URL firmato viene rinnovato un po' prima della scadenza: una lezione lunga non
// deve interrompersi a metà perché il link è scaduto durante la riproduzione.
const URL_RENEW_MARGIN_MS = 5 * 60 * 1000;

/**
 * Player con blocco del salto in avanti e progresso tracciato lato server.
 *
 * Il video è un file ospitato dalla piattaforma. Poiché un tag <video> non può
 * inviare l'header Authorization, la sorgente è un URL firmato a scadenza ottenuto
 * da GET /lessons/{id}/video-url. Con storage S3 quella stessa chiamata restituisce
 * una presigned URL: qui non cambia nulla.
 *
 * Props: lesson { id, has_video }
 *        onProgress(progressState) — callback opzionale per il componente padre
 */
export default function LockedVideoPlayer({ lesson, onProgress }) {
  const videoRef = useRef(null);
  const maxWatchedRef = useRef(0);
  const durationRef = useRef(0);
  const revertingRef = useRef(false);
  const renewTimerRef = useRef(null);

  const [src, setSrc] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [progress, setProgress] = useState({ percent: 0, max: 0, duration: 0, completed: false });
  const [notice, setNotice] = useState("");

  const postProgress = useCallback(async (current, duration) => {
    try {
      const { data } = await api.post(`/lessons/${lesson.id}/progress`, {
        current_seconds: current,
        duration_seconds: duration || durationRef.current || 0,
      });
      // Il server è la fonte di verità sulla percentuale (è lui a concedere il
      // completamento), ma non deve far arretrare il massimo locale: se la risposta
      // arriva con ritardo, la riproduzione nel frattempo è andata avanti e un
      // arretramento provocherebbe un falso "salto in avanti" al controllo successivo.
      // Fa eccezione il caso in cui il server abbia rifiutato un salto: lì si riallinea.
      maxWatchedRef.current = data.seek_forward_blocked
        ? data.max_watched_seconds
        : Math.max(maxWatchedRef.current, data.max_watched_seconds);
      if (data.video_duration_seconds > 0) durationRef.current = data.video_duration_seconds;
      const state = {
        percent: data.percent_watched,
        max: data.max_watched_seconds,
        duration: data.video_duration_seconds,
        completed: data.is_completed,
      };
      setProgress(state);
      onProgress?.(state);
      if (data.seek_forward_blocked) {
        setNotice("Non puoi saltare avanti: il progresso deve essere consecutivo.");
      }
    } catch (e) {
      // Errori transitori ignorati: il prossimo heartbeat riproverà
    }
  }, [lesson.id, onProgress]);

  /**
   * Unico punto che decide se la posizione corrente è legittima.
   *
   * Tre casi:
   * - indietro o già visto  → consentito, non si tocca il massimo
   * - avanti di poco        → riproduzione normale: **il massimo avanza**
   * - avanti di molto       → salto sulla timeline: si torna al massimo visto
   *
   * L'avanzamento del massimo deve avvenire qui e non solo alla risposta del
   * server: l'heartbeat parte ogni 5s e nel frattempo la riproduzione supererebbe
   * la soglia, facendosi riportare indietro di continuo.
   */
  const checkPosition = useCallback(() => {
    const video = videoRef.current;
    if (!video || revertingRef.current) return;
    const t = video.currentTime;
    const max = maxWatchedRef.current;

    if (t <= max) return; // riguardare una parte già vista è sempre permesso

    if (t - max <= SEEK_JUMP_THRESHOLD) {
      maxWatchedRef.current = t; // avanzamento naturale della riproduzione
      return;
    }

    revertingRef.current = true;
    setNotice("Non puoi saltare avanti: il progresso deve essere consecutivo.");
    video.currentTime = max;
    // Il riposizionamento genera a sua volta 'seeking'/'timeupdate': senza questa
    // finestra il controllo si richiamerebbe a vicenda.
    setTimeout(() => { revertingRef.current = false; }, 300);
  }, []);

  // --- Recupero (e rinnovo) dell'URL firmato ------------------------------
  const fetchSignedUrl = useCallback(async () => {
    const { data } = await api.get(`/lessons/${lesson.id}/video-url`);
    setSrc(data.url);
    if (renewTimerRef.current) clearTimeout(renewTimerRef.current);
    const renewInMs = Math.max(60_000, data.expires_in * 1000 - URL_RENEW_MARGIN_MS);
    renewTimerRef.current = setTimeout(() => {
      // Rinnovo silenzioso: si riprende dal punto corrente senza interrompere l'utente
      fetchSignedUrl().catch(() => {});
    }, renewInMs);
    return data.url;
  }, [lesson.id]);

  useEffect(() => {
    let cancelled = false;
    let heartbeat = null;

    const setup = async () => {
      setLoading(true);
      setError("");
      setNotice("");
      setSrc("");

      // Progresso già registrato per questa lezione
      try {
        const { data } = await api.get(`/lessons/${lesson.id}/progress`);
        maxWatchedRef.current = data.max_watched_seconds || 0;
        durationRef.current = data.video_duration_seconds || 0;
        setProgress({
          percent: data.percent_watched || 0,
          max: data.max_watched_seconds || 0,
          duration: data.video_duration_seconds || 0,
          completed: !!data.is_completed,
        });
      } catch (e) {
        // non bloccante
      }
      if (cancelled) return;

      try {
        await fetchSignedUrl();
      } catch (e) {
        if (cancelled) return;
        setError(
          e?.response?.status === 404
            ? "Il video di questa lezione non è ancora stato caricato."
            : "Impossibile caricare il video. Riprova più tardi."
        );
        setLoading(false);
        return;
      }

      heartbeat = setInterval(() => {
        const video = videoRef.current;
        if (!video || video.paused || video.ended) return;
        postProgress(video.currentTime, video.duration || durationRef.current);
      }, HEARTBEAT_MS);
    };

    setup();

    return () => {
      cancelled = true;
      if (heartbeat) clearInterval(heartbeat);
      if (renewTimerRef.current) clearTimeout(renewTimerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lesson.id]);

  // --- Handler del tag <video> -------------------------------------------
  const handleLoadedMetadata = (e) => {
    const video = e.currentTarget;
    durationRef.current = video.duration || durationRef.current;
    // Riprende da dove si era arrivati
    if (maxWatchedRef.current > 5 && maxWatchedRef.current < video.duration) {
      video.currentTime = maxWatchedRef.current;
    }
    setLoading(false);
  };

  const handlePause = (e) => {
    if (revertingRef.current) return;
    postProgress(e.currentTarget.currentTime, e.currentTarget.duration);
  };

  const handleEnded = (e) => {
    const video = e.currentTarget;
    // A fine riproduzione `currentTime` si ferma tipicamente qualche decimo prima
    // di `duration`: inviando la durata piena si registra il traguardo reale,
    // altrimenti la lezione resterebbe a 99.x% pur essendo stata vista tutta.
    maxWatchedRef.current = video.duration || maxWatchedRef.current;
    postProgress(video.duration || video.currentTime, video.duration);
  };

  const handleError = () => {
    // Sorgente scaduta o non raggiungibile: un rinnovo risolve il caso più comune
    fetchSignedUrl().catch(() => {
      setError("Riproduzione interrotta. Ricarica la pagina per riprendere.");
    });
  };

  const pct = progress.percent || 0;
  const isCompleted = progress.completed || pct >= COMPLETION_THRESHOLD;

  // Lezione senza video caricato: stato esplicito, non un player rotto
  if (lesson.has_video === false && !src) {
    return (
      <div
        data-testid={`video-missing-${lesson.id}`}
        className="w-full bg-brand-bg border border-dashed border-brand-line rounded-sm aspect-video flex flex-col items-center justify-center text-center px-6"
      >
        <VideoOff size={28} className="text-brand-muted mb-3" />
        <div className="text-brand-ink font-semibold mb-1">Video non ancora disponibile</div>
        <p className="text-sm text-brand-muted max-w-sm">
          Il video di questa lezione non è ancora stato caricato. Riprova più tardi o
          contatta la tua amministrazione.
        </p>
      </div>
    );
  }

  return (
    <div data-testid={`locked-video-player-${lesson.id}`} className="w-full space-y-3">
      <div className="relative bg-black aspect-video rounded-sm overflow-hidden">
        {loading && !error && (
          <div className="absolute inset-0 flex items-center justify-center bg-black text-white z-10">
            <Loader2 size={22} className="animate-spin mr-2" /> Caricamento video…
          </div>
        )}

        {error ? (
          <div
            data-testid={`video-error-${lesson.id}`}
            className="absolute inset-0 flex flex-col items-center justify-center bg-brand-ink text-white px-6 text-center"
          >
            <AlertTriangle size={26} className="text-brand-gold mb-2" />
            <p className="text-sm max-w-sm">{error}</p>
          </div>
        ) : (
          <video
            ref={videoRef}
            data-testid={`video-element-${lesson.id}`}
            src={src || undefined}
            className="w-full h-full"
            controls
            playsInline
            preload="metadata"
            // Nasconde il pulsante di download del browser. Non è una protezione:
            // l'URL firmato consegna comunque i byte a chi li vuole.
            controlsList="nodownload"
            disablePictureInPicture
            onLoadedMetadata={handleLoadedMetadata}
            onSeeking={checkPosition}
            onTimeUpdate={checkPosition}
            onPause={handlePause}
            onEnded={handleEnded}
            onError={handleError}
            onContextMenu={(e) => e.preventDefault()}
          />
        )}

        {isCompleted && (
          <div className="absolute top-2 right-2 z-20 rounded-sm bg-emerald-600 text-white text-xs font-semibold px-2 py-1 inline-flex items-center gap-1">
            <CheckCircle2 size={12} /> Completata
          </div>
        )}
      </div>

      {/* Barra di progresso */}
      <div>
        <div className="flex items-center justify-between text-xs text-brand-muted mb-1.5">
          <span className="inline-flex items-center gap-1.5" data-testid={`progress-label-${lesson.id}`}>
            <Lock size={12} className={isCompleted ? "text-emerald-600" : "text-brand-blue"} />
            Visto: <strong className={isCompleted ? "text-emerald-700" : "text-brand-blue"}>{Math.round(pct)}%</strong>
            {!isCompleted && <span>· richiesto {COMPLETION_THRESHOLD}%</span>}
          </span>
          {notice && <span className="text-amber-700 text-xs">{notice}</span>}
        </div>
        <div className="h-2 bg-brand-line rounded-sm overflow-hidden">
          <div
            className={"h-full transition-all " + (isCompleted ? "bg-emerald-500" : "bg-brand-blue")}
            style={{ width: `${Math.min(pct, 100)}%` }}
            data-testid={`progress-bar-${lesson.id}`}
          />
        </div>
        <div className="text-[10px] text-brand-muted mt-1">
          Il progresso è tracciato lato server. Salti in avanti non consentiti.
        </div>
      </div>
    </div>
  );
}
